import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/functions/api';
import { useSocket } from './SocketContext';
import AnnouncementPopup from '../components/Announcement/AnnouncementPopup';
import AnnouncementListDialog from '../components/Announcement/AnnouncementListDialog';

const apiBase = process.env.REACT_APP_API_URL;
const PRIORITY_RANK = { critical: 0, important: 1, normal: 2 };
const SOCKET_EVENTS = ['announcement:created', 'announcement:updated', 'announcement:deleted'];

// หน้าต่างแยก (New Window) ของ popup โครงการ/โอกาสทางธุรกิจ — ไม่ต้องเด้งประกาศซ้ำในหน้าต่างพวกนี้
const POPUP_WINDOW_RE = /^\/(project|marketing)-popup\//;

const isSnoozed = (a) => !!a.snoozed_until && new Date(a.snoozed_until) > new Date();

const AnnouncementContext = createContext({
  items: [],
  unreadCount: 0,
  openList: () => {},
  refresh: () => {},
});

export const useAnnouncements = () => useContext(AnnouncementContext);

export function AnnouncementProvider({ children }) {
  const { pathname } = useLocation();
  const isPopupWindow = POPUP_WINDOW_RE.test(pathname);
  const socket = useSocket();

  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]); // id ของประกาศที่รอเด้ง popup ตามลำดับ
  const [queueTotal, setQueueTotal] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const queuedRef = useRef(new Set()); // id ที่เคยเข้าคิวแล้วในรอบนี้ กันเด้งซ้ำตอน refresh
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const refresh = useCallback(async () => {
    const { data } = await api.get(`${apiBase}/announcement/mine`);
    const rows = Array.isArray(data) ? data : [];
    setItems(rows);
    return rows;
  }, []);

  // ทุกประกาศที่ยังไม่หมดอายุเด้งทุกครั้งที่เปิดเว็บ (รับทราบแล้วก็ยังเด้ง) ยกเว้นที่ติ๊กงดไว้ 1 วัน (เก็บใน DB ต่อบัญชี) —
  // แต่ critical ที่ยังไม่รับทราบเด้งเสมอแม้เคยงดไว้
  const enqueuePopups = useCallback((rows) => {
    const fresh = rows
      .filter((a) => !queuedRef.current.has(String(a.id))
        && ((a.priority === 'critical' && !a.acknowledged_at) || !isSnoozed(a)))
      .sort((a, b) => (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority])
        || (new Date(b.createdAt) - new Date(a.createdAt)));
    if (!fresh.length) return;
    fresh.forEach((a) => queuedRef.current.add(String(a.id)));
    setQueue((q) => [...q, ...fresh.map((a) => String(a.id))]);
    setQueueTotal((t) => t + fresh.length);
  }, []);

  useEffect(() => {
    refresh().then((rows) => {
      if (!isPopupWindow) enqueuePopups(rows);
    }).catch(() => {});
    // โหลดครั้งเดียวตอนเข้าเว็บ — เปลี่ยนหน้าไม่ต้องโหลดใหม่ (ประกาศใหม่/แก้ไขมาทาง socket แทน)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // มีการสร้าง/แก้ไข/ลบประกาศ → โหลดรายการใหม่ แล้วเด้ง popup เฉพาะประกาศที่เพิ่งมองเห็นได้ (สร้างใหม่, เปิดประกาศ
  // อีกครั้ง, เพิ่ม user เข้ากลุ่มผู้รับ) — ประกาศที่ถูกปิด/ลบ/ตัดออกจากกลุ่มผู้รับหลุดออกจากคิวไปเอง
  useEffect(() => {
    if (!socket) return undefined;
    const onChange = async () => {
      const known = new Set(itemsRef.current.map((a) => String(a.id)));
      const rows = await refresh();
      const visible = new Set(rows.map((a) => String(a.id)));
      setQueue((q) => {
        const next = q.filter((id) => visible.has(id));
        const removed = q.length - next.length;
        if (removed) setQueueTotal((t) => (next.length ? t - removed : 0));
        return next;
      });
      if (!isPopupWindow) enqueuePopups(rows.filter((a) => !known.has(String(a.id))));
    };
    const handler = () => { onChange().catch(() => {}); };
    SOCKET_EVENTS.forEach((ev) => socket.on(ev, handler));
    return () => SOCKET_EVENTS.forEach((ev) => socket.off(ev, handler));
  }, [socket, isPopupWindow, refresh, enqueuePopups]);

  const markSeen = useCallback((a) => {
    if (!a || a.seen_at) return;
    setItems((rows) => rows.map((r) => (r.id === a.id ? { ...r, seen_at: new Date().toISOString() } : r)));
    api.post(`${apiBase}/announcement/${a.id}/seen`).catch(() => {});
  }, []);

  const currentId = queue[0];
  const current = items.find((a) => String(a.id) === currentId);

  useEffect(() => {
    markSeen(current);
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const popQueue = (id) => {
    setQueue((q) => {
      const next = q.filter((x) => x !== String(id));
      if (!next.length) setQueueTotal(0);
      return next;
    });
  };

  const acknowledge = useCallback(async (a) => {
    await api.post(`${apiBase}/announcement/${a.id}/acknowledge`);
    const now = new Date().toISOString();
    setItems((rows) => rows.map((r) => (r.id === a.id
      ? { ...r, seen_at: r.seen_at || now, acknowledged_at: now }
      : r)));
  }, []);

  const snooze = useCallback(async (a) => {
    const { data } = await api.post(`${apiBase}/announcement/${a.id}/snooze`);
    setItems((rows) => rows.map((r) => (r.id === a.id ? { ...r, snoozed_until: data?.snoozed_until } : r)));
  }, []);

  const closePopup = async (a, { acknowledge: ack, snooze: wantSnooze }) => {
    if (ack) {
      await acknowledge(a);
    }
    if (wantSnooze) {
      await snooze(a);
    }
    popQueue(a.id);
  };

  const value = useMemo(() => ({
    items,
    unreadCount: items.filter((a) => !a.acknowledged_at).length,
    openList: () => setListOpen(true),
    refresh,
  }), [items, refresh]);

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
      {current && !listOpen && (
        <AnnouncementPopup
          announcement={current}
          position={queueTotal - queue.length + 1}
          total={queueTotal}
          onClose={closePopup}
        />
      )}
      <AnnouncementListDialog
        open={listOpen}
        items={items}
        onClose={() => setListOpen(false)}
        onOpenItem={markSeen}
        onAcknowledge={acknowledge}
      />
    </AnnouncementContext.Provider>
  );
}

export default AnnouncementContext;
